export interface CartAddOn {
  title: string
  quantity: number
  cost: number
  /** The cashier's add-on category, e.g. "Ekstra til pizza". Absent on older
   *  cashier builds, in which case the add-ons render as a plain list. */
  categoryTitle?: string
}

export interface CartItem {
  title: string
  quantity: number
  cost: number
  currency?: string
  addOns?: CartAddOn[]
  /** Ingredients the customer asked to leave out, e.g. ["Tomat", "Mozzarella"].
   *  The cashier shows these on its own cart; the display must too, so the
   *  customer can check the order before paying. */
  removedIngredients?: string[]
}

export interface Cart {
  items?: CartItem[]
  itemsAmount?: number
  discountAmount?: number
  vatAmount?: number
  cashAmount?: number
  totalAmount?: number
  currency?: string
}

export interface Place {
  title?: string
  tagline?: string
  logo?: string
  currency?: string
  google_place_id?: string
}

export type DisplayMessage =
  | { type: 'cart_update'; cart: Cart }
  | { type: 'scan_mobilepay_qr_code'; url: string; amount: number }
  | { type: 'scan_receipt_qr_code'; url: string; amount: number }
  | { type: 'change_given'; change: number; received?: number; currency?: string }
  | { type: 'cashback_given'; cashback: number }
  | { type: 'payment_error'; message: string }

export type DisplayMessageType = DisplayMessage['type']

/** Compile-time exhaustiveness guard: adding a DisplayMessage variant without
 *  handling it becomes a type error at every switch that uses this. */
export function assertNever(value: never): never {
  throw new Error(`Unhandled DisplayMessage: ${JSON.stringify(value)}`)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function isDisplayMessage(value: unknown): value is DisplayMessage {
  if (!isRecord(value)) return false

  switch (value.type) {
    case 'cart_update':
      return isRecord(value.cart)
    case 'scan_mobilepay_qr_code':
    case 'scan_receipt_qr_code':
      return typeof value.url === 'string' && typeof value.amount === 'number'
    case 'change_given':
      return typeof value.change === 'number'
    case 'cashback_given':
      return typeof value.cashback === 'number'
    case 'payment_error':
      return typeof value.message === 'string'
    default:
      return false
  }
}

const PLACE_STRINGS = ['title', 'tagline', 'logo', 'currency', 'google_place_id'] as const

/**
 * Normalises a raw place record into a `Place`, keeping the record and
 * dropping only the individual fields that are not strings (e.g. a numeric
 * shop name), rather than rejecting the whole record. Returns `null` only
 * when `value` is not an object at all.
 */
export function toPlace(value: unknown): Place | null {
  if (!isRecord(value)) return null

  const place: Place = {}
  for (const key of PLACE_STRINGS) {
    const field = value[key]
    if (typeof field === 'string') place[key] = field
  }
  return place
}

/** Normalises an RTDB-sourced list into an array: an object-with-numeric-keys
 *  (a sparse or non-contiguously-keyed list, e.g. after a mid-cart item
 *  deletion) becomes an array of its values in ascending numeric-key order;
 *  a real array passes through; anything else unusable becomes an empty
 *  array; `undefined` (nothing there yet) is left as-is. */
function toArray<T>(value: unknown): T[] | undefined {
  if (value === undefined) return undefined
  if (Array.isArray(value)) return value as T[]
  if (isRecord(value)) {
    return Object.keys(value)
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => value[key]) as T[]
  }
  return []
}

/** Normalises a single cart item's `addOns` the same way `cart.items` is
 *  normalised, so a keyed-object add-ons list doesn't throw when rendered. */
function normalizeCartItem(item: CartItem): CartItem {
  return {
    ...item,
    addOns: toArray<CartAddOn>(item.addOns),
    // RTDB serialises a sparse list as a keyed object, same as cart.items.
    removedIngredients: toArray<string>(item.removedIngredients)?.filter(
      (ingredient): ingredient is string => typeof ingredient === 'string',
    ),
  }
}

/** Normalises a validated `DisplayMessage` at the boundary so downstream
 *  code can trust `cart.items` (and each item's `addOns`) is always an
 *  array. */
export function normalizeDisplayMessage(message: DisplayMessage): DisplayMessage {
  if (message.type !== 'cart_update') return message
  const items = toArray<CartItem>(message.cart.items)?.map(normalizeCartItem)
  return { ...message, cart: { ...message.cart, items } }
}
